import { useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { api } from "../../lib/api";
import { track } from "../../lib/analytics";
import { useAuth } from "../../context/AuthContext";

export const SaveCalculation = ({ calc, values, results }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const loggedIn = !!user;

  const save = async () => {
    setSaving(true);
    track("save_clicked", { calculator: calc.slug });
    try {
      if (loggedIn) {
        await api.post("/products", {
          name: label || calc.name,
          calculator: calc.slug,
          inputs: values,
          net_profit: results.netProfit,
          margin_pct: results.marginPct,
          marketplace: calc.feeMarketplace || null,
        });
        toast.success("Saved to My Products", {
          description: "Track its profit anytime from your dashboard.",
          icon: <Check className="h-4 w-4" />,
        });
      } else {
        await api.post("/saved-calculations", {
          calculator: calc.slug, inputs: values, results, label: label || calc.name,
        });
        toast.success("Calculation saved", {
          description: "Create a free account to track all your products in one dashboard.",
        });
      }
      setOpen(false);
      setLabel("");
    } catch {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full" data-testid="save-calculation-trigger">
          <Bookmark className="mr-2 h-4 w-4" /> {loggedIn ? "Save to My Products" : "Save this calculation"}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="save-dialog">
        <DialogHeader>
          <DialogTitle>{loggedIn ? "Save to My Products" : "Save this calculation"}</DialogTitle>
          <DialogDescription>
            {loggedIn
              ? "Give this product a name so you can track its profit and margin in your dashboard."
              : "Save it now. Create a free account to track products and compare their profitability."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="save-label">Product name</Label>
          <Input
            id="save-label"
            data-testid="save-label-input"
            placeholder={calc.name}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-row">
          {!loggedIn && (
            <Button asChild variant="ghost" className="sm:mr-auto" data-testid="save-signin-link">
              <Link to="/account">Sign in to track</Link>
            </Button>
          )}
          <Button onClick={save} disabled={saving} data-testid="save-confirm-button">
            {saving ? "Saving…" : loggedIn ? "Save product" : "Save calculation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
